import React, { useState, useEffect } from "react";
import qs from 'query-string';
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "../../services/toast";
import { Formik, Form, Field } from "formik";
import usePlans from "../../hooks/usePlans";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { alpha } from "@mui/material/styles";
import { makeStyles } from 'tss-react/mui';
import { i18n } from "../../translate/i18n";
import logo from "../../assets/logologin.png";
import { openApi } from "../../services/api";
import toastError from "../../errors/toastError";
import moment from "moment";
import wallpaper from "../../assets/wallpaper.jpg";
import wallpaperVideo from "../../assets/wallpaper.mp4";

const TRIAL_DAYS = 3;

const PLAN_CARD_WIDTH = 250;
const PLAN_GAP = 16;
const CARD_PADDING_X = 64;
const MIN_CARD_WIDTH = 440;
const MAX_CARD_WIDTH = 880;

const Copyright = () => (
	<Typography
		variant="body2"
		color="textSecondary"
		align="center"
		style={{ fontSize: 12 }}
	>
		{"© "}
		{new Date().getFullYear()}{" "}
		<Link
			color="inherit"
			underline="hover"
			href="https://www.linkedin.com/in/luigy-miranda"
			target="_blank"
			rel="noopener noreferrer"
		>
			ChatEcarga
		</Link>
		{" · Todos los derechos reservados"}
	</Typography>
);

const useStyles = makeStyles()((theme) => ({
	root: {
		minHeight: "100vh",
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		position: "relative",
		overflow: "hidden",
		padding: theme.spacing(4, 2),
		backgroundColor: "#101216",
	},
	video: {
		position: "absolute",
		top: 0,
		left: 0,
		width: "100%",
		height: "100%",
		objectFit: "cover",
	},
	overlay: {
		position: "absolute",
		inset: 0,
		backgroundColor: "rgba(16, 18, 22, 0.72)",
	},
	card: {
		position: "relative",
		padding: theme.spacing(5, 4, 3),
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		width: "100%",
		backgroundColor: theme.palette.background.paper,
		boxShadow: "0 20px 50px rgba(0,0,0,0.45)",
		borderRadius: 16,
		transition: theme.transitions.create("max-width", {
			duration: theme.transitions.duration.standard,
		}),
	},
	logo: {
		width: 150,
		marginBottom: theme.spacing(1),
	},
	title: {
		fontSize: 20,
		fontWeight: 700,
	},
	form: {
		width: "100%",
		marginTop: theme.spacing(3),
	},
	trialBanner: {
		display: "flex",
		alignItems: "flex-start",
		gap: theme.spacing(1.5),
		padding: theme.spacing(1.75, 2),
		borderRadius: 10,
		backgroundColor: alpha("#158f47", 0.08),
		border: `1px solid ${alpha("#158f47", 0.25)}`,
		marginBottom: theme.spacing(3),
	},
	trialIcon: {
		fontSize: 20,
		color: "#158f47",
		flexShrink: 0,
		marginTop: 1,
	},
	trialTitle: {
		fontSize: 13.5,
		fontWeight: 700,
		color: theme.palette.text.primary,
	},
	trialText: {
		fontSize: 12.5,
		color: theme.palette.text.secondary,
		marginTop: 2,
	},
	sectionLabel: {
		fontSize: 12,
		fontWeight: 700,
		letterSpacing: 0.6,
		textTransform: "uppercase",
		color: theme.palette.text.secondary,
		marginBottom: theme.spacing(1.5),
	},
	plansRow: {
		display: "flex",
		flexWrap: "wrap",
		gap: theme.spacing(2),
	},
	planCard: {
		position: "relative",
		flex: "1 1 250px",
		display: "flex",
		flexDirection: "column",
		alignItems: "stretch",
		justifyContent: "flex-start",
		textAlign: "left",
		padding: theme.spacing(2.5, 2),
		borderRadius: 12,
		border: `2px solid ${theme.palette.divider}`,
		transition: "border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease",
		"&:hover": {
			borderColor: theme.palette.primary.light,
			transform: "translateY(-2px)",
			boxShadow: "0 6px 18px rgba(16,24,40,0.10)",
		},
	},
	planCardSelected: {
		borderColor: theme.palette.primary.main,
		backgroundColor: alpha(theme.palette.primary.main, 0.05),
		boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.18)}`,
	},
	planCheck: {
		position: "absolute",
		top: 10,
		right: 10,
		fontSize: 20,
		color: theme.palette.primary.main,
	},
	planName: {
		fontSize: 15,
		fontWeight: 700,
		color: theme.palette.text.primary,
	},
	priceRow: {
		display: "flex",
		alignItems: "baseline",
		gap: 2,
		marginTop: theme.spacing(0.5),
	},
	currency: {
		fontSize: 16,
		fontWeight: 600,
		color: theme.palette.text.primary,
	},
	price: {
		fontSize: 32,
		fontWeight: 700,
		lineHeight: 1.1,
		color: theme.palette.text.primary,
	},
	period: {
		fontSize: 13,
		color: theme.palette.text.secondary,
		marginLeft: 2,
	},
	planDivider: {
		height: 1,
		backgroundColor: theme.palette.divider,
		margin: theme.spacing(1.75, 0),
	},
	featureList: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(0.75),
	},
	featureItem: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		fontSize: 13,
		color: theme.palette.text.secondary,
	},
	featureIcon: {
		fontSize: 15,
		color: "#158f47",
		flexShrink: 0,
	},
	plansEmpty: {
		fontSize: 13,
		color: theme.palette.text.secondary,
		fontStyle: "italic",
		padding: theme.spacing(2, 0),
	},
	planError: {
		fontSize: 12,
		color: theme.palette.error.main,
		marginTop: theme.spacing(1),
	},
	actionRow: {
		display: "flex",
		gap: theme.spacing(1.5),
		margin: theme.spacing(3, 0, 1),
	},
	backButton: {
		height: 42,
		fontSize: 14,
		flexShrink: 0,
	},
	submitButton: {
		height: 42,
		fontSize: 14,
		flex: 1,
	},
	link: {
		marginTop: theme.spacing(1.5),
		fontSize: 13,
	},
	copyright: {
		marginTop: theme.spacing(3),
	},
}));

const UserSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Demasiado corto!")
		.max(50, "Demasiado largo!")
		.required("Requerido"),
	password: Yup.string()
		.min(5, "Demasiado corto!")
		.max(50, "Demasiado largo!")
		.required("Requerido"),
	email: Yup.string().email("Email inválido").required("Requerido"),
	planId: Yup.mixed().required("Elige un plan para continuar"),
});

const DATA_FIELDS = ["name", "email", "password"];

const planFeatures = (plan) => {
	const plural = (n, singular, pluralWord) =>
		`${n} ${n === 1 ? singular : pluralWord}`;

	const features = [
		plural(plan.users, "usuario", "usuarios"),
		plural(plan.connections, "conexión de WhatsApp", "conexiones de WhatsApp"),
		plural(plan.queues, "departamento", "departamentos"),
	];

	const optional = [
		[plan.useSchedules, "Agendamientos"],
		[plan.useCampaigns, "Campañas"],
		[plan.useInternalChat, "Chat interno"],
		[plan.useKanban, "Kanban"],
		[plan.useOpenAi, "Open.AI"],
		[plan.useIntegrations, "Integraciones"],
		[plan.useExternalApi, "API externa"],
	];
	optional.forEach(([enabled, label]) => {
		if (enabled) features.push(label);
	});

	return features;
};

const SignUp = () => {
	const { classes, cx } = useStyles();
	const navigate = useNavigate();
	const params = qs.parse(window.location.search);
	const companyId = params.companyId || null;

	const initialState = { name: "", email: "", password: "", planId: "" };
	const [user] = useState(initialState);
	const [step, setStep] = useState(0);
	const dueDate = moment().add(TRIAL_DAYS, "days").format();

	const handleSignUp = async values => {
		const newValues = {
			...values,
			recurrence: "MENSAL",
			dueDate,
			status: "t",
			campaignsEnabled: true,
		};
		try {
			await openApi.post("/companies/cadastro", newValues);
			toast.success(i18n.t("signup.toasts.success"));
			navigate("/login");
		} catch (err) {
			console.log(err);
			toastError(err);
		}
	};

	const [plans, setPlans] = useState([]);
	const { list: listPlans } = usePlans();

	const plansWidth =
		plans.length * PLAN_CARD_WIDTH +
		Math.max(0, plans.length - 1) * PLAN_GAP +
		CARD_PADDING_X;
	const cardMaxWidth =
		step === 0
			? MIN_CARD_WIDTH
			: Math.min(MAX_CARD_WIDTH, Math.max(MIN_CARD_WIDTH, plansWidth));

	useEffect(() => {
		async function fetchData() {
			const list = await listPlans();
			setPlans(list);
		}
		fetchData();
	}, []);

	const goToPlans = async (validateForm, setTouched) => {
		const errors = await validateForm();
		const stepErrors = DATA_FIELDS.filter((field) => errors[field]);
		if (stepErrors.length) {
			setTouched({ name: true, email: true, password: true });
			return;
		}
		setStep(1);
	};

	return (
		<div className={classes.root}>
			<CssBaseline />
			<video
				className={classes.video}
				autoPlay
				loop
				muted
				playsInline
				poster={wallpaper}
			>
				<source src={wallpaperVideo} type="video/mp4" />
			</video>
			<div className={classes.overlay} />
			<Paper className={classes.card} style={{ maxWidth: cardMaxWidth }}>
				<img src={logo} alt="Logo" className={classes.logo} />
				<Typography component="h1" className={classes.title}>
					¡Regístrate gratis!
				</Typography>

				<Formik
					initialValues={user}
					enableReinitialize={true}
					validationSchema={UserSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSignUp(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({
						touched,
						errors,
						values,
						setFieldValue,
						setTouched,
						validateForm,
						submitCount,
					}) => (
						<Form
							className={classes.form}
							onKeyDown={(e) => {
								if (step === 0 && e.key === "Enter") {
									e.preventDefault();
									goToPlans(validateForm, setTouched);
								}
							}}
						>
							{step === 0 ? (
								<>
									<Grid container spacing={2}>
										<Grid size={12}>
											<Field
												as={TextField}
												autoComplete="organization"
												name="name"
												error={touched.name && Boolean(errors.name)}
												helperText={touched.name && errors.name}
												variant="outlined"
												fullWidth
												id="name"
												label="Nombre de la Empresa"
												autoFocus
											/>
										</Grid>
										<Grid size={12}>
											<Field
												as={TextField}
												variant="outlined"
												fullWidth
												id="email"
												label={i18n.t("signup.form.email")}
												name="email"
												error={touched.email && Boolean(errors.email)}
												helperText={touched.email && errors.email}
												autoComplete="email"
												required
											/>
										</Grid>
										<Grid size={12}>
											<Field
												as={TextField}
												variant="outlined"
												fullWidth
												name="password"
												error={touched.password && Boolean(errors.password)}
												helperText={touched.password && errors.password}
												label={i18n.t("signup.form.password")}
												type="password"
												id="password"
												autoComplete="new-password"
												required
											/>
										</Grid>
									</Grid>
									<div className={classes.actionRow}>
										<Button
											type="button"
											fullWidth
											variant="contained"
											color="primary"
											className={classes.submitButton}
											onClick={() => goToPlans(validateForm, setTouched)}
										>
											Siguiente
										</Button>
									</div>
								</>
							) : (
								<>
									<div className={classes.trialBanner}>
										<AutoAwesomeIcon className={classes.trialIcon} />
										<div>
											<div className={classes.trialTitle}>
												Empiezas con {TRIAL_DAYS} días de prueba gratis
											</div>
											<div className={classes.trialText}>
												Elige el plan que mejor te quede y pruébalo sin apuro.
												Puedes cambiarlo más adelante.
											</div>
										</div>
									</div>

									<Typography className={classes.sectionLabel}>
										Elige tu plan
									</Typography>
									<div className={classes.plansRow}>
										{plans.map((plan) => {
											const selected = values.planId === plan.id;
											return (
												<ButtonBase
													key={plan.id}
													focusRipple
													className={cx(
														classes.planCard,
														selected && classes.planCardSelected
													)}
													onClick={() => setFieldValue("planId", plan.id)}
												>
													{selected && (
														<CheckCircleIcon className={classes.planCheck} />
													)}
													<span className={classes.planName}>{plan.name}</span>
													<div className={classes.priceRow}>
														<span className={classes.currency}>$</span>
														<span className={classes.price}>{plan.value}</span>
														<span className={classes.period}>/mes</span>
													</div>
													<div className={classes.planDivider} />
													<div className={classes.featureList}>
														{planFeatures(plan).map((feature) => (
															<span
																key={feature}
																className={classes.featureItem}
															>
																<CheckIcon className={classes.featureIcon} />
																{feature}
															</span>
														))}
													</div>
												</ButtonBase>
											);
										})}
										{!plans.length && (
											<span className={classes.plansEmpty}>
												No hay planes disponibles por el momento.
											</span>
										)}
									</div>
									{submitCount > 0 && errors.planId && (
										<Typography className={classes.planError}>
											{errors.planId}
										</Typography>
									)}

									<div className={classes.actionRow}>
										<Button
											type="button"
											variant="outlined"
											color="secondary"
											className={classes.backButton}
											startIcon={<ArrowBackIcon />}
											onClick={() => setStep(0)}
										>
											Atrás
										</Button>
										<Button
											type="submit"
											variant="contained"
											color="primary"
											className={classes.submitButton}
										>
											Crear mi cuenta gratis
										</Button>
									</div>
								</>
							)}

							<Grid container sx={{
								justifyContent: "center"
							}}>
								<Grid>
									<Link
										variant="body2"
										component={RouterLink}
										to="/login"
										className={classes.link}
										underline="hover"
										sx={{ color: "text.secondary", "&:hover": { color: "primary.main" } }}
									>
										{i18n.t("signup.buttons.login")}
									</Link>
								</Grid>
							</Grid>
						</Form>
					)}
				</Formik>
				<Box className={classes.copyright}>
					<Copyright />
				</Box>
			</Paper>
		</div>
	);
};

export default SignUp;
